import { MessageSquare, Minimize2, X, Trash2, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChatBubble } from './chat/ChatBubble';
import { ChatMessage } from './chat/ChatMessage';
import { ChatInput } from './chat/ChatInput';
import { ContextIndicator } from './chat/ContextIndicator';
import { LoadingIndicator } from './chat/LoadingIndicator';
import { ErrorMessage } from './chat/ErrorMessage';
import { useLeadChatbot } from '@/hooks/useLeadChatbot';
import { useIsMobile } from '@/hooks/use-mobile';
import { useSidebar } from '@/context/SidebarContext';
import type { Lead } from '@/types/lead';
import { useState, useEffect } from 'react';

// Quick Actions Component
const QuickActions = ({ onActionClick, disabled, contextData, isOpen, setIsOpen }: { 
  onActionClick: (prompt: string) => void; 
  disabled: boolean;
  contextData: any;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}) => {
  const getContextualActions = () => {
    const actions = [
      { 
        label: '📊 Summarize', 
        prompt: 'Provide a comprehensive summary of this lead including their business, key contacts, recent activities, and current status.',
        color: 'bg-gradient-to-br from-blue-500/10 to-blue-500/5 hover:from-blue-500/20 hover:to-blue-500/10 border-blue-500/20'
      },
      { 
        label: '🎯 Decision Makers', 
        prompt: 'Analyze the contacts and suggest who the key decision makers might be for this company.',
        color: 'bg-gradient-to-br from-purple-500/10 to-purple-500/5 hover:from-purple-500/20 hover:to-purple-500/10 border-purple-500/20'
      },
      { 
        label: '🔍 Research', 
        prompt: 'Search for the latest news and updates about this company. What recent developments should I know about?',
        color: 'bg-gradient-to-br from-green-500/10 to-green-500/5 hover:from-green-500/20 hover:to-green-500/10 border-green-500/20'
      },
      { 
        label: '💡 Outreach Plan', 
        prompt: 'Based on all the information available, suggest an effective outreach strategy for engaging with this lead.',
        color: 'bg-gradient-to-br from-orange-500/10 to-orange-500/5 hover:from-orange-500/20 hover:to-orange-500/10 border-orange-500/20'
      },
    ];

    if (contextData?.hasNotes) {
      actions.push({
        label: '📝 Analyze Notes',
        prompt: 'Analyze all the notes for this lead and provide key insights and action items.',
        color: 'bg-gradient-to-br from-yellow-500/10 to-yellow-500/5 hover:from-yellow-500/20 hover:to-yellow-500/10 border-yellow-500/20'
      });
    }

    if (contextData?.hasTasks) {
      actions.push({
        label: '✅ Task Summary',
        prompt: 'Summarize the current tasks, their status, and upcoming deadlines.',
        color: 'bg-gradient-to-br from-cyan-500/10 to-cyan-500/5 hover:from-cyan-500/20 hover:to-cyan-500/10 border-cyan-500/20'
      });
    }

    return actions;
  };

  const actions = getContextualActions();

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger className="w-full px-4 py-2 border-t bg-gradient-to-r from-muted via-muted to-muted/50 hover:from-muted hover:to-muted transition-all flex items-center justify-between group">
        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Quick Actions</span>
        <ChevronDown className={`w-3 h-3 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="px-4 py-3 bg-gradient-to-b from-muted/50 to-transparent border-b">
          <div className="grid grid-cols-2 gap-2">
            {actions.map((action) => (
              <button
                key={action.label}
                onClick={() => {
                  onActionClick(action.prompt);
                  setIsOpen(false);
                }}
                disabled={disabled}
                className={`px-3 py-2 text-xs font-medium rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed border ${action.color} shadow-sm hover:shadow-md`}
              >
                {action.label}
              </button>
            ))}
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

interface LeadChatbotProps {
  lead: Lead;
}

export const LeadChatbot = ({ lead }: LeadChatbotProps) => {
  const isMobile = useIsMobile();
  const { setOnOpenChat } = useSidebar();
  const [quickActionsOpen, setQuickActionsOpen] = useState(true);
  const {
    messages,
    isLoading,
    isContextLoading,
    contextData,
    error,
    isOpen,
    messagesEndRef,
    sendMessage,
    toggleChatOpen,
    clearError,
    clearConversation,
  } = useLeadChatbot(lead);

  // Register chat toggle with sidebar context so MobileActionBar can trigger it
  useEffect(() => {
    setOnOpenChat(toggleChatOpen);
    return () => setOnOpenChat(undefined);
  }, [toggleChatOpen, setOnOpenChat]);

  // Floating button when closed
  if (!isOpen) {
    return <ChatBubble isOpen={isOpen} onToggle={toggleChatOpen} />;
  }

  // Chat window when open
  return (
    <>
      {/* Backdrop for mobile */}
      {isMobile && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 animate-in fade-in"
          onClick={toggleChatOpen}
        />
      )}

      {/* Chat Card */}
      <Card
        className={`
          fixed z-50 shadow-2xl
          ${isMobile 
            ? 'inset-4 animate-in slide-in-from-bottom-10' 
            : 'bottom-20 right-5 w-[400px] h-[600px] animate-in slide-in-from-bottom-5'
          }
        `}
      >
        {/* Header */}
        <CardHeader className="border-b p-4 flex flex-row items-center justify-between space-y-0">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-primary" />
            <div>
              <h3 className="font-semibold text-sm">Chat about {lead.name}</h3>
              <p className="text-xs text-muted-foreground">AI Assistant</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {messages.length > 0 && (
              <Button
                variant="ghost"
                size="icon"
                onClick={clearConversation}
                className="h-8 w-8"
                title="Clear conversation"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleChatOpen}
              className="h-8 w-8"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>

        {/* Context Indicator */}
        <ContextIndicator
          isLoading={isContextLoading}
          contextData={contextData}
          error={error}
        />

        {/* Messages Area */}
        <CardContent className="p-0 flex flex-col h-[calc(100%-140px)]">
          <ScrollArea className="flex-1">
            {messages.length === 0 && !isLoading && (
              <div className="flex flex-col items-center justify-center h-full p-8 text-center">
                <MessageSquare className="w-12 h-12 text-muted-foreground/50 mb-4" />
                <h4 className="font-medium mb-2">Start a conversation</h4>
                <p className="text-sm text-muted-foreground">
                  Ask me anything about this lead. I have access to all their data, notes, tasks, and can perform web searches if needed.
                </p>
              </div>
            )}

            {messages.map((message) => (
              <ChatMessage key={message.id} message={message} />
            ))}

            {isLoading && <LoadingIndicator />}

            {error && (
              <ErrorMessage
                message={error}
                onDismiss={clearError}
              />
            )}

            <div ref={messagesEndRef} />
          </ScrollArea>

          {/* Quick Actions - Always visible, collapsible */}
          <QuickActions
            onActionClick={(prompt) => sendMessage(prompt, false)}
            disabled={isLoading}
            contextData={contextData}
            isOpen={quickActionsOpen}
            setIsOpen={setQuickActionsOpen}
          />

          {/* Input */}
          <ChatInput
            onSendMessage={sendMessage}
            isLoading={isLoading}
            placeholder={`Ask about ${lead.name}...`}
          />
        </CardContent>
      </Card>
    </>
  );
};
